import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue'
import EventoService from '@/services/EventoService'
import { adminPotenciaGuard } from '@/shareds/sharedsPotencia'
import { adminLojaGuard } from '@/shareds/sharedsLoja'
import { IEvento } from '@/../common/types/Evento'
import ComponentAuthGuard from '@/components/ComponentAuthGuard.vue'
import { IPresenca, InputPresenca } from '@/../common/types/Presenca'
import PresencaService from '@/services/PresencaService'
import IconeDoEvento from '@/components/IconeDoEvento.vue'
import { formatarData } from '@/shareds/formatadores'
import BotoesDePresenca from '@/components/eventos/BotoesDePresenca.vue'
import { obterCargoDeAdminDaLoja } from './../../../common/shareds/sharedsIrmao'
import RelatorioService from '@/services/RelatorioService'
// @ts-ignore
import arraySort from 'array-sort'
import { alert, agenda, authModule, userModule } from '@/store'
import { criarPresenca, irmaoPodeVerPresenca, obterCorDoEvento } from '@/shareds/sharedsEvento'
import moment from 'moment'
import { getErrorMessage } from '@/helpers/error-helpers'
import { dataEHoraDoEvento } from '../../../common/shareds/sharedEvento'
import { obterCorDoStatusDoCertificado } from '@/shareds/sharedCertificado'
import { CertificadoDetalhado } from 'common/types/certificado/Certificado'
import DialogoDeAprovacaoDoCertificado from '@/components/certificado/DialogoDeAprovacaoDoCertificado.vue'
import { CARGOS_QUE_PODEM_GERAR_CERTIFICADO } from '@/../common/shareds/sharedCertificado'
import { éCargoDaLojaAtivo } from '@/../common/shareds/sharedLoja'
import { TipoDeOrdenacaoDeEvento } from 'common/types/Relatorio'
import TextoComLinks from '@/components/TextoComLinks.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'DetalharEvento',
  props: {
	id: { type: String, required: true },
},
  setup(__props) {

const props = __props;



const carregando = ref(true)
const erro = ref<string | null>(null)
const evento = ref<IEvento | null>(null)
const mostraAprovacaoDoCertificado = ref(false)
const certificado = ref<CertificadoDetalhado | null>(null)

const buscandoPresencas = ref(true)
const erroPresencas = ref<string | null>(null)
const presencas = ref<IPresenca[]>([])
const inputPresenca = ref<InputPresenca | null>(null)

const opcoesDeOrdenacao = [
	{ descricao: 'Ordem Alfabética', valor: 'alfabetica', icone: 'mdi-sort-alphabetical-ascending' },
	{ descricao: 'Cargo/Relevância', valor: 'relevancia', icone: 'mdi-school' },
] satisfies { descricao: string, valor: TipoDeOrdenacaoDeEvento, icone: string }[]

const autorizacoesDoEvento = computed(() => {
	if (!evento.value) return []
	switch (evento.value.tipoDaSessao.abreviacao) {
		case 'FDJ':
		case 'DEMOL': {
			const { currentUser } = userModule
			return currentUser && currentUser.role !== 'Visitante' && currentUser.secretarioDeInformatica
		}
	}
	switch (evento.value.tipoDaSessao.tipoDaEntidade) {
		case 'somar': return ['admin']
		case 'potencia': return [adminPotenciaGuard(evento.value.entidade._id)]
		case 'loja': return [adminLojaGuard(evento.value.entidade._id)]
		default: return []
	}
})

function autorizacoesDeExclusao(presenca: IPresenca) {
	if (presenca.certificado) {
		return false
	}
	return autorizacoesDoEvento.value
}

const autorizacaoDeCertificado = computed(() => {
	const { currentUser } = userModule
	return currentUser &&
		currentUser.role !== 'Visitante' &&
		evento.value &&
		éCargoDaLojaAtivo(currentUser, CARGOS_QUE_PODEM_GERAR_CERTIFICADO, evento.value.entidade._id)
})

const presencasOrdenadas = computed(() => arraySort(presencas.value, [
	(presencaA: IPresenca, presencaB: IPresenca) => presencaB.intencoes.length - presencaA.intencoes.length,
	'irmao.nomeCompleto',
]))

const passouDaData = computed(() => {
	if (!evento.value) return true
	return moment().diff(dataEHoraDoEvento(evento.value)) >= 0
})

const mostraPresenca = computed(() => irmaoPodeVerPresenca(evento.value!, userModule.currentUser))

async function buscar() {
	try {
		carregando.value = true
		evento.value = await EventoService.get(props.id)
	} catch (error) {
		erro.value = getErrorMessage(error)
	} finally {
		carregando.value = false
	}
}

async function buscarPresencas() {
	const usuario = userModule.currentUser!
	try {
		buscandoPresencas.value = true
		presencas.value = await PresencaService.getByEvento(props.id)
		const presenca = presencas.value.find(presenca => presenca.irmao._id === usuario._id)

		inputPresenca.value = presenca
			? {
				...presenca,
				irmao: presenca.irmao._id,
			}
			: criarPresenca({
				evento: props.id,
				irmao: usuario._id,
			})
	} catch (error) {
		erroPresencas.value = getErrorMessage(error)
	} finally {
		buscandoPresencas.value = false
	}
}

buscar()
buscarPresencas()

function gerarRelatorio(ordenacao: TipoDeOrdenacaoDeEvento) {
	if (!evento.value) return
	const nomeRelatorio = `${evento.value.entidade.nome.replaceAll(' ','_')}_${evento.value.data}_${Date.now()}`
	const url = RelatorioService.getRelatorioDeEvento(nomeRelatorio, {
		evento: props.id,
		ordenacao,
	})
	window.open(url, '_blank')
}

function trocarPresenca(novaPresenca: InputPresenca) {
	inputPresenca.value = novaPresenca
	if (!userModule.currentUser) throw new Error('No current user')
	if (userModule.currentUser.role === 'Visitante') return

	const indiceDaPresenca = presencas.value.findIndex(presenca => presenca.irmao._id === novaPresenca.irmao)
	if (!novaPresenca.intencoes.length) {
		presencas.value.splice(indiceDaPresenca, 1)
		return
	}

	const presenca: IPresenca = {
		_id: novaPresenca._id!,
		intencoes: novaPresenca.intencoes,
		irmao: userModule.currentUser!,
		evento: novaPresenca.evento,
	}
	if (indiceDaPresenca === -1) {
		presencas.value.push(presenca)
	} else {
		presencas.value.splice(indiceDaPresenca, 1, presenca)
	}
}

async function removerPresenca(presenca: IPresenca) {
	try {
		if (!confirm(`Deseja remover ${presenca.irmao.nomeCompleto} desse evento?`)) return
		await PresencaService.remove(presenca._id)
		presencas.value = presencas.value
			.filter(({ _id }) => _id !== presenca._id)
		if (presenca.irmao._id === userModule.currentUser?._id) {
			inputPresenca.value = criarPresenca({
				evento: props.id,
				irmao: userModule.currentUser._id
			})
		}
	} catch (error) {
		alert.setError(error)
	}
}

function selecionarCertificadoDaPresenca(presenca: IPresenca) {
	if (!presenca.certificado) {
		alert.setError('Presenca não possui certificado')
		return
	}

	if (!evento.value) {
		alert.setError('Evento não carregado')
		return
	}

	certificado.value = {
		...presenca.certificado,
		irmao: presenca.irmao,
		evento: evento.value,
	}
	mostraAprovacaoDoCertificado.value = true
}

function aoAlterarCertificado(certificadoAlterado: CertificadoDetalhado) {
	presencas.value = presencas.value.map(presenca => {
		return presenca?.certificado?._id === certificadoAlterado._id
			? { ...presenca, certificado: certificadoAlterado }
			: presenca
	})
	mostraAprovacaoDoCertificado.value = false

	alert.setMessage({
		type: certificadoAlterado.status === 'aprovado' ? 'success' : 'info',
		text: `Certificado do Ir∴ ${certificadoAlterado.irmao.nomeCompleto} ${certificadoAlterado.status}`
	})
}

return { __sfc: true,props, carregando, erro, evento, mostraAprovacaoDoCertificado, certificado, buscandoPresencas, erroPresencas, presencas, inputPresenca, opcoesDeOrdenacao, autorizacoesDoEvento, autorizacoesDeExclusao, autorizacaoDeCertificado, presencasOrdenadas, passouDaData, mostraPresenca, buscar, buscarPresencas, gerarRelatorio, trocarPresenca, removerPresenca, selecionarCertificadoDaPresenca, aoAlterarCertificado, ComponentAuthGuard, IconeDoEvento, formatarData, BotoesDePresenca, obterCargoDeAdminDaLoja, agenda, authModule, obterCorDoEvento, obterCorDoStatusDoCertificado, DialogoDeAprovacaoDoCertificado, TextoComLinks }
}

})